import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import { t } from 'ember-intl';
import {} from 'lumen/utils/constants/benchmark-insight/los/dropdown-los';

export const OCCUPANCY_VIEWS = {
  ACTUALS: 'actuals',
  MIX: 'mix',
} as const;
export type OccupancyView =
  (typeof OCCUPANCY_VIEWS)[keyof typeof OCCUPANCY_VIEWS];

export type DropdownOccupancyViewOption = Option & {
  id: OccupancyView;
  label: string;
};

interface HotelBenchmarkInsightDropdownsDropdownOccupancyViewSignature {
  Args: {
    changeSelectedOccupancyView: (view: OccupancyView) => void;
    selectedOccupancyView: OccupancyView;
  };
}

export default class HotelBenchmarkInsightDropdownsDropdownOccupancyView extends Component<HotelBenchmarkInsightDropdownsDropdownOccupancyViewSignature> {
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @cached
  get options(): DropdownOccupancyViewOption[] {
    return [
      {
        id: OCCUPANCY_VIEWS.ACTUALS,
        label: this.intl.t('benchmark_insight.los_chart.view_type.actuals'),
      },
      {
        id: OCCUPANCY_VIEWS.MIX,
        label: this.intl.t('benchmark_insight.los_chart.view_type.mix'),
      },
    ];
  }

  get selectedOccupancyViewOption(): DropdownOccupancyViewOption {
    return (
      this.options.find(
        (option) => option.id === this.args.selectedOccupancyView,
      ) ?? {
        id: OCCUPANCY_VIEWS.ACTUALS,
        label: this.intl.t('benchmark_insight.los_chart.view_type.actuals'),
      }
    );
  }

  @action
  changeSelectedOccupancyView(option: DropdownOccupancyViewOption): void {
    if (option.id === this.args.selectedOccupancyView) {
      return;
    }

    this.args.changeSelectedOccupancyView(option.id);

    this.usageAnalytics.track('Primary Chart Axis Changed', {
      eventTrigger: 'los',
      product: 'benchmark-insight',
      type: option.id,
    });
  }

  <template>
    <span
      class="benchmark-insight__chart-breakdown__axis-buttons__los-option-dropdown__label text-200--regular"
    >{{t "general.view_by"}}</span>
    <PrismSelectInput
      @isSmall={{true}}
      @onChange={{this.changeSelectedOccupancyView}}
      @options={{this.options}}
      @value={{this.selectedOccupancyViewOption}}
    />
  </template>
}
